import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// @mui
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemText from '@mui/material/ListItemText';
import Notifications from '@mui/icons-material/Notifications';
import Help from '@mui/icons-material/Help';
import Drafts from '@mui/icons-material/Drafts';

// core
import { useGlobal } from '_core/hooks/useGlobal';
import { withLang } from '_core/hocs/withLang';
import { Menu } from '_core/components/Menu';
import { Loading } from '_core/components/Loading';

// style
import { useStyles } from './headerControlsStyle';

const EVENT_TYPES = {
  ticket: { icon: Help, uri: '/tickets/{id}' },

  message: { icon: Drafts },

  _default: { icon: Drafts }
};

const RawUserNotifier = (props) => {
  const { getLangString: l } = props;

  const classes = useStyles();

  const {
    request: { queue },
    config: { API_METHOD, API_NOTICE }
  } = useGlobal();

  const [eventData, setEventData] = useState();

  const fetchEvents = () => {
    queue(API_METHOD.VISITOR_EVENT_LIST, {})
      .then((result) => {
        if (result) {
          setEventData(result);
        }
      })
      .catch(() => {})
      .finally(() => {
        // Устанавливаем total: 0 только если данных нет
        setEventData((prev) => prev || { total: 0 });
      });
  };

  React.useEffect(fetchEvents, []);

  React.useEffect(() => {
    const notifyListener = (event) =>
      (event.detail.name === API_NOTICE.TICKET_STATUS ||
        event.detail.name === API_NOTICE.TICKET_MESSAGE) &&
      fetchEvents();

    window.addEventListener('notify', notifyListener);

    return () => window.removeEventListener('notify', notifyListener);
  }, []);

  const renderButton = (btnProps) => (
    <IconButton {...btnProps}>
      <Badge
        classes={{ badge: classes.userNotifierBadge }}
        color="primary"
        badgeContent={eventData?.total}
        max={99}
      >
        <Notifications />
      </Badge>
    </IconButton>
  );

  const renderItem = ({ onPopoverClose }, item) => {
    const type = EVENT_TYPES[item.type] || EVENT_TYPES._default;

    const Icon = type.icon;

    const onClick = () => {
      setEventData((state) => {
        const stateList = state.list.filter((m) => m.id !== item.id);

        return {
          total: state.total - state.list.length + stateList.length,
          list: stateList
        };
      });

      onPopoverClose();
    };

    return (
      <ListItem
        button
        key={`${item.type}-${item.id}-${item.message}`}
        component={Link}
        to={type.uri?.replace('{id}', item.id)}
        onClick={onClick}
      >
        <ListItemIcon>
          <Icon />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography className={classes.userNotifierListItemPrimary} noWrap>
              {String(item.excerpt || '')
                .trim()
                .replace(/[\n\r][\s\S]*/, '…')}
            </Typography>
          }
          secondary={item.time.format}
        />
      </ListItem>
    );
  };

  const renderContent = (props) => (
    <List
      component="nav"
      subheader={
        <ListSubheader component="div">
          {l('r._.header.notifier')}

          {!eventData ? <Loading /> : null}
        </ListSubheader>
      }
    >
      {eventData ? (
        eventData.list?.length ? (
          eventData.list.map((item) => renderItem(props, item))
        ) : (
          <ListItem button>
            <ListItemText secondary={l('r._.header.notifier.none')} />
          </ListItem>
        )
      ) : null}
    </List>
  );

  return (
    <Menu
      ButtonComponent={renderButton}
      renderContent={renderContent}
      tooltip={l('r._.header.notifier')}
      paperProps={{ className: classes.userNotifierPaper }}
      popoverProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      }}
    />
  );
};

RawUserNotifier.defaultProps = {};

RawUserNotifier.propTypes = {
  // `withLang` HOC props
  langInfo: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired
};

export const UserNotifier = withLang(RawUserNotifier);
